import React, { FC, useEffect, useRef, useState } from 'react';
import { connect, MapStateToProps } from 'react-redux';
import classNames from 'classnames';
import { compose } from '@reduxjs/toolkit';

import type { BettingContextProps } from '../../../../../../types';
import type { ReduxState } from 'app/redux/types';

import { withinBettingContext } from 'app/contexts/betting';
import { Hint } from 'app/design-system/components/Popper';
import { useTranslation } from 'app/lang';
import { accessMainScore } from 'app/lib/betting/accessMatchDetails';
import { TeamId } from 'app/lib/betting/constants';
import usePrevious from 'app/lib/hooks/usePrevious';

import { ScoreboardCell } from '../ScoreboardCell';

import dictionary from './MainScoreWrapper.dictionary.json';

// ---

type MainScoreWrapperProps = {
  matchId: number;
};

type StoreProps = {
  firstMainScore: null | number;
  secondMainScore: null | number;
};

const Index: FC<MainScoreWrapperProps & StoreProps> = ({
  firstMainScore,
  secondMainScore,
}) => {
  const { t } = useTranslation();

  const firstPreviousScore = usePrevious(firstMainScore);
  const secondPreviousScore = usePrevious(secondMainScore);
  const [showBgAnimation, setBgAnimation] = useState(false);
  const bgTimeout = useRef<number>();

  useEffect(() => {
    if (firstPreviousScore !== firstMainScore || secondPreviousScore !== secondMainScore) {
      setBgAnimation(true);

      bgTimeout.current = window.setTimeout(() => {
        setBgAnimation(false);
      }, 1500);
    }

    return () => {
      bgTimeout.current && clearTimeout(bgTimeout.current);
    };
  }, [firstMainScore, secondMainScore]);

  return (
    <li className="main-score-wrapper">
      <Hint content={t(dictionary.mainPayment)}
            childrenWrapperClassName={classNames(
              'score-divider scoreboard-cells-wrapper',
              showBgAnimation && 'backgroundAnimation',
            )}
            childrenWrapperTag="div"
            placement="top">
        <ScoreboardCell className="match-main-score">
          <span>{firstMainScore}</span>
        </ScoreboardCell>
        <ScoreboardCell className="match-main-score">
          <span>{secondMainScore}</span>
        </ScoreboardCell>
      </Hint>
    </li>
  );
};

const mapStateToProps: MapStateToProps<
  StoreProps,
  MainScoreWrapperProps & BettingContextProps,
  ReduxState
> = (state, {
  $betting: {
    content: {
      selectors: { matches },
    },
  },
  matchId,
}) => {
  const match = matches(state).get(matchId);
  const mainScores = accessMainScore(match);

  if (!mainScores) {
    return {
      firstMainScore: null,
      secondMainScore: null,
    };
  }

  return {
    firstMainScore: mainScores.get(TeamId.FIRST, null),
    secondMainScore: mainScores.get(TeamId.SECOND, null),
  };
};

export default compose<FC<MainScoreWrapperProps>>(
  withinBettingContext,
  connect(mapStateToProps),
)(Index);
